import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { appBaseUrl } from "@/config";
import { AuthShell } from "./AuthShell";

/** Sign-in AND self-serve sign-up (the landing page drives here). New accounts are
 *  auto-provisioned as PENDING by the backend and land on the awaiting-approval screen. */
export function SignInScreen() {
  const { signInEmail, signUpEmail, signInGoogle, error } = useAuth();
  const nav = useNavigate();

  // The desk lives on app.earlywire.io — signing in on the APEX would mint a Firebase session
  // on the wrong origin (signed out the moment they reach the app) and strand invite/ref codes
  // in apex storage. Old invite links and organic hits land here; bounce them to the app origin
  // with the query AND any locally-captured codes intact. appBaseUrl() is "" on app/dev hosts.
  useEffect(() => {
    const base = appBaseUrl();
    if (!base) return;
    const p = new URLSearchParams(window.location.search);
    try {
      const ref = localStorage.getItem("ew_ref");
      const invite = localStorage.getItem("ew_invite");
      if (ref && !p.get("ref")) p.set("ref", ref);
      if (invite && !p.get("invite")) p.set("invite", invite);
    } catch { /* storage unavailable */ }
    const qs = p.toString();
    window.location.replace(`${base}/signin${qs ? `?${qs}` : ""}`);
  }, []);
  // ?confirmed=1|0 — the email-confirmation link lands here (possibly in a fresh browser).
  const [confirmedFlag] = useState(() => new URLSearchParams(window.location.search).get("confirmed"));
  const [mode, setMode] = useState<"signin" | "signup">(confirmedFlag ? "signin" : "signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function onEmail(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") await signUpEmail(email, password);
      else await signInEmail(email, password);
      nav("/ops");
    } catch {
      /* error surfaced via context */
    } finally {
      setBusy(false);
    }
  }

  async function onGoogle() {
    setBusy(true);
    try {
      await signInGoogle();
      nav("/ops");
    } catch {
      /* surfaced */
    } finally {
      setBusy(false);
    }
  }

  const signup = mode === "signup";

  return (
    <AuthShell
      title={signup ? "CREATE YOUR ACCOUNT" : "SECURE SIGN-IN"}
      subtitle={signup ? "Free hour on the live desk · no card · instant access" : "Operator authentication required"}
    >
      {confirmedFlag === "1" && (
        <div className="auth-copy" style={{ color: "var(--green)" }}>
          ✓ Email confirmed — sign in and your free hour starts now.
        </div>
      )}
      {localStorage.getItem("ew_invite") && (
        <div className="auth-copy" style={{ color: "var(--green)" }}>
          ✓ Invite detected — your access unlocks the moment you sign up.
        </div>
      )}
      {confirmedFlag === "0" && (
        <div className="auth-error">
          That confirmation link is invalid or already used. Sign in — if you're still unconfirmed
          you can resend from the next screen.
        </div>
      )}
      <form className="auth-form" onSubmit={onEmail}>
        <label className="auth-field">
          <span className="auth-label">Operator Email</span>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <label className="auth-field">
          <span className="auth-label">Passphrase</span>
          <input
            type="password"
            autoComplete={signup ? "new-password" : "current-password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        {error && <div className="auth-error">{error}</div>}
        <button className="auth-btn primary" type="submit" disabled={busy}>
          {busy ? "WORKING…" : signup ? "CREATE ACCOUNT" : "ESTABLISH UPLINK"}
        </button>
      </form>
      <div className="auth-divider">
        <span>OR</span>
      </div>
      <button className="auth-btn" onClick={onGoogle} disabled={busy}>
        CONTINUE WITH GOOGLE
      </button>
      <p className="auth-foot">
        {signup ? (
          <>Already an operator?{" "}
            <a href="#signin" onClick={(e) => { e.preventDefault(); setMode("signin"); }} style={{ color: "var(--green)" }}>Sign in</a>
          </>
        ) : (
          <>New here?{" "}
            <a href="#signup" onClick={(e) => { e.preventDefault(); setMode("signup"); }} style={{ color: "var(--green)" }}>Create an account</a>
          </>
        )}
      </p>
      <p className="auth-foot">
        By continuing you agree to the <a href="/terms" style={{ color: "var(--ivory-d)" }}>Terms of Service</a> —
        informational signals, not investment advice.
      </p>
    </AuthShell>
  );
}
